import React from 'react';
import { Link } from 'react-router-dom';

/* ===== IMAGES ===== */
const IMG = {
  hero: '/artistigianato_hero.png',
  blacksmith: '/artistigianato_blacksmith.png',
  biblioteca: '/artistigianato_biblioteca.png',
  festival: '/artistigianato_festival.png',
  bottegaPasta: '/bottega_pasta.png',
  bottegaArtigiano: '/bottega_artigiano.png',
  bottegaUncinetto: '/bottega_uncinetto.png',
  fonti: '/valdelsa_fonti.png',
  sbandieratori: '/valdelsa_sbandieratori.png',
  castello: '/valdelsa_castello.png',
  torriColori: '/valdelsa_torri_colori.png',
  tuscany: '/borgo_casole.png',
};

/* ===== CSS ===== */
const css = `
@keyframes fadeInUp { from { opacity:0; transform:translateY(30px); } to { opacity:1; transform:translateY(0); } }
@keyframes glowPulse { 0%,100% { text-shadow:0 0 20px rgba(0,255,65,0.3); } 50% { text-shadow:0 0 60px rgba(0,255,65,0.7),0 0 120px rgba(0,255,65,0.3); } }
@keyframes scanline { 0%{ top:-10%; } 100%{ top:110%; } }
`;

/* Full-width image section */
const Img = ({ src, alt, children, overlay = 'bg-black/60', h = 'min-h-[50vh]', id }) => (
  <div className={`relative ${h} overflow-hidden`} data-testid={id}>
    <img src={src} alt={alt} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
    <div className={`absolute inset-0 ${overlay}`} />
    <div className="relative z-10 h-full flex items-center">{children}</div>
  </div>
);

/* Aphorism strip — always present, never invasive */
const Aforisma = ({ text, author, bg = '#050505' }) => (
  <div className="py-10 px-4 text-center" style={{ background: bg }} data-testid="aforisma">
    <p className="font-mono font-black text-base md:text-lg text-white/90 max-w-3xl mx-auto italic">
      "{text}"
    </p>
    {author && <p className="text-zinc-500 font-mono text-xs mt-2">— {author}</p>}
  </div>
);

/* ===== MAIN ===== */
const ArtistigianatoPage = () => (
  <div className="min-h-screen bg-[#050505] pt-20 pb-0">
    <style>{css}</style>

    {/* ======================================== */}
    {/* HERO — IL FESTIVAL */}
    {/* ======================================== */}
    <Img src={IMG.hero} alt="ARTISTIGIANATO — Festival delle Arti Perdute" overlay="bg-gradient-to-t from-[#050505] via-black/40 to-black/20" h="min-h-[80vh]" id="artistigianato-hero">
      <div className="w-full text-center px-4 mt-auto pb-14">
        <p className="font-mono text-xs uppercase tracking-[0.5em] text-[#00FF41] mb-4" style={{ animation: 'glowPulse 3s ease-in-out infinite' }}>
          &lt;/AI_covo.tech&gt;&reg; <span className="block text-[9px] tracking-[0.3em] mt-1 opacity-70">DIGITAL HUB SOCIALE</span> presenta
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black font-mono text-white mb-4"
          style={{ animation: 'fadeInUp 1s ease-out', textShadow: '0 4px 40px rgba(0,0,0,0.8)', letterSpacing: '0.05em' }}>
          ARTIST<span className="text-[#D4A574]">IGIANATO</span>
        </h1>
        <p className="text-white/80 text-base md:text-lg max-w-2xl mx-auto font-mono mb-2" style={{ animation: 'fadeInUp 1.2s ease-out' }}>
          Artisti + Artigianato + Le Arti Perdute e Tramandate
        </p>
        <p className="text-zinc-400 text-sm font-mono" style={{ animation: 'fadeInUp 1.4s ease-out' }}>
          Meta Settembre — Valdelsa — Il Festival che salva il sapere
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3" style={{ animation: 'fadeInUp 1.6s ease-out' }}>
          <span className="bg-[#D4A574]/15 border border-[#D4A574]/40 text-[#D4A574] font-mono text-xs px-4 py-2 rounded-full">Dal Medioevo a Oggi</span>
          <span className="bg-[#D4A574]/15 border border-[#D4A574]/40 text-[#D4A574] font-mono text-xs px-4 py-2 rounded-full">Laboratori Aperti a Tutti</span>
          <span className="bg-[#D4A574]/15 border border-[#D4A574]/40 text-[#D4A574] font-mono text-xs px-4 py-2 rounded-full">Interattivi e Didattici</span>
        </div>
      </div>
    </Img>

    {/* AFORISMA 1 */}
    <Aforisma text="La tradizione non e la cenere del fuoco. E la trasmissione della fiamma." author="Tommaso Moro" />

    {/* ======================================== */}
    {/* IL MANIFESTO — PERCHE QUESTO FESTIVAL */}
    {/* ======================================== */}
    <div className="max-w-4xl mx-auto px-4 py-16 text-center" data-testid="manifesto-artistigianato">
      <p className="text-[#00FF41] font-mono text-xs uppercase tracking-[0.3em] mb-6">Il Manifesto</p>
      <h2 className="text-2xl md:text-3xl font-black font-mono text-white mb-6 leading-relaxed">
        Ogni meta settembre, quando la scuola riapre e il solito tran tran ricomincia,
        <span className="text-[#D4A574]"> la Valdelsa si risveglia</span>.
      </h2>
      <p className="text-zinc-400 text-base leading-relaxed mb-6 max-w-3xl mx-auto">
        Le strade, le piazze, i cortili si riempiono di botteghe a cielo aperto.
        Fabbri, tessitori, cestai, cuoche, falegnami — i maestri delle arti perdute escono dalle case
        e aprono le porte del loro sapere. Non per nostalgia. Per sopravvivenza.
      </p>
      <p className="text-white font-mono font-black text-lg md:text-xl max-w-2xl mx-auto">
        Perche un ragazzino di 12 anni <span className="text-[#D4A574]">DEVE</span> poter sapere
        come si fa il pane, come si intreccia un cesto, come si batte il ferro.
      </p>
    </div>

    {/* ======================================== */}
    {/* BLACKSMITH — IL PASSAGGIO */}
    {/* ======================================== */}
    <Img src={IMG.blacksmith} alt="Fabbro insegna al ragazzo" overlay="bg-gradient-to-r from-black/75 via-black/40 to-transparent" h="min-h-[55vh]" id="section-passaggio">
      <div className="max-w-5xl mx-auto px-6 w-full">
        <h2 className="text-3xl md:text-4xl font-black font-mono text-white mb-4" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}>
          Il Passaggio
        </h2>
        <p className="text-white/90 max-w-lg text-sm md:text-base leading-relaxed mb-4">
          Le mani vecchie guidano le mani giovani. Il ferro si piega, la fiamma riscalda,
          il sapere passa. Non c'e tutorial che tenga — alcune cose si imparano solo cosi.
          Occhi negli occhi. Mani nelle mani.
        </p>
        <p className="font-mono font-bold text-sm text-[#D4A574]">
          Ad ARTISTIGIANATO, ogni bottega e un ponte tra chi sa e chi vuole imparare.
        </p>
      </div>
    </Img>

    {/* AFORISMA 2 */}
    <Aforisma text="Siamo nani sulle spalle di giganti. Vediamo piu lontano solo perche siamo sollevati dalla loro grandezza." author="Bernardo di Chartres, XII secolo" />

    {/* ======================================== */}
    {/* LE BOTTEGHE DEL FESTIVAL */}
    {/* ======================================== */}
    <div className="max-w-5xl mx-auto px-4 py-16" data-testid="botteghe-festival">
      <div className="text-center mb-12">
        <p className="text-[#D4A574] font-mono text-xs uppercase tracking-[0.3em] mb-2">Dal Medioevo a Oggi</p>
        <h2 className="text-2xl md:text-3xl font-black font-mono text-white mb-3">
          Le Botteghe del Festival
        </h2>
        <p className="text-zinc-400 text-sm max-w-xl mx-auto">
          Storie di un tempo che fu. Laboratori interattivi e didattici dove tocchi, impari, e porti a casa un pezzo di storia.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <div className="relative rounded-xl overflow-hidden group" data-testid="bottega-cucina-fest">
          <img src={IMG.bottegaPasta} alt="Cucina contadina" className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
          <div className="absolute bottom-0 p-5">
            <h3 className="font-mono font-black text-lg text-white mb-1">La Cucina Contadina</h3>
            <p className="text-zinc-300 text-xs leading-relaxed">Pici, ribollita, pappa al pomodoro, schiacciata. La cucina povera toscana che ha sfamato generazioni. Impasti con le tue mani.</p>
          </div>
        </div>
        <div className="relative rounded-xl overflow-hidden group" data-testid="bottega-artigiano-fest">
          <img src={IMG.bottegaArtigiano} alt="L'officina" className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
          <div className="absolute bottom-0 p-5">
            <h3 className="font-mono font-black text-lg text-white mb-1">L'Officina Medievale</h3>
            <p className="text-zinc-300 text-xs leading-relaxed">Ferro battuto, legno intagliato, cestini di vimini, cappelli di paglia. Le mani che sanno creare dal nulla.</p>
          </div>
        </div>
        <div className="relative rounded-xl overflow-hidden group" data-testid="bottega-arti-fest">
          <img src={IMG.bottegaUncinetto} alt="Arti tessili" className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
          <div className="absolute bottom-0 p-5">
            <h3 className="font-mono font-black text-lg text-white mb-1">Le Arti del Filo</h3>
            <p className="text-zinc-300 text-xs leading-relaxed">Uncinetto, maglia, ricamo, tessitura. Le donne della Valdelsa insegnano l'arte della pazienza e della bellezza.</p>
          </div>
        </div>
      </div>

      {/* All workshops grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {[
          { name: 'Fabbro & Ferro Battuto', era: 'Medioevo' },
          { name: 'Cestai & Vimini', era: 'Rinascimento' },
          { name: 'Pasta Fresca a Mano', era: 'Tradizione' },
          { name: 'Intaglio del Legno', era: 'Medioevo' },
          { name: 'Tessitura al Telaio', era: 'Rinascimento' },
          { name: 'Cappelli di Paglia', era: '800' },
          { name: 'Uncinetto & Ricamo', era: 'Tradizione' },
          { name: 'Ceramica & Terracotta', era: 'Etruschi' },
          { name: 'Conserve & Sottoli', era: 'Tradizione' },
          { name: 'Orto & Potatura', era: 'Contadino' },
          { name: 'Panificazione', era: 'Medioevo' },
          { name: 'Affilatura Coltelli', era: 'Tradizione' },
        ].map(w => (
          <div key={w.name} className="bg-[#0A0A0A] border border-[#D4A574]/20 rounded-lg p-4 hover:border-[#D4A574]/50 transition-colors">
            <p className="font-mono font-bold text-sm text-white mb-1">{w.name}</p>
            <p className="text-[#D4A574] font-mono text-[10px] uppercase tracking-wider">{w.era}</p>
          </div>
        ))}
      </div>
    </div>

    {/* PANORAMA TOSCANA */}
    <Img src={IMG.tuscany} alt="Colline toscane" overlay="bg-black/30" h="min-h-[25vh]">
      <div className="w-full text-center px-4">
        <p className="text-white font-mono font-black text-base md:text-xl" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}>
          Ogni strada della Valdelsa diventa un'aula. Ogni piazza, un laboratorio.
        </p>
      </div>
    </Img>

    {/* AFORISMA 3 */}
    <Aforisma text="Non ereditiamo la terra dai nostri padri. La prendiamo in prestito dai nostri figli." author="Proverbio Nativo Americano" />

    {/* ======================================== */}
    {/* BIBLIOTECA DIGITALE */}
    {/* ======================================== */}
    <Img src={IMG.biblioteca} alt="Il ragazzo tra il libro antico e il tablet" overlay="bg-gradient-to-l from-black/75 via-black/40 to-black/20" h="min-h-[55vh]" id="biblioteca-digitale">
      <div className="max-w-5xl mx-auto px-6 w-full flex justify-end">
        <div className="max-w-lg text-right">
          <p className="text-[#00FF41] font-mono text-xs uppercase tracking-[0.3em] mb-3">Il Progetto</p>
          <h2 className="text-3xl md:text-4xl font-black font-mono text-white mb-4" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}>
            BIBLIOTECA<br /><span className="text-[#00FF41]">DIGITALE</span>
          </h2>
          <p className="text-white/90 text-sm md:text-base leading-relaxed">
            Una mano sul libro antico. Una mano sul tablet.
            Il ponte tra il sapere di ieri e il mondo di domani.
          </p>
        </div>
      </div>
    </Img>

    <div className="max-w-4xl mx-auto px-4 py-16 text-center" data-testid="biblioteca-content">
      <p className="text-white font-mono font-black text-xl md:text-2xl mb-6 leading-relaxed max-w-3xl mx-auto">
        "Si, ma... <span className="text-[#D4A574]">noi quando saremo anziani,
        cosa lasceremo</span> alle nuove generazioni?"
      </p>
      <p className="text-zinc-400 text-base leading-relaxed mb-6 max-w-3xl mx-auto">
        La domanda sorge spontanea. E la risposta e il progetto piu ambizioso del Covo:
        una raccolta di <span className="text-white font-bold">tutte — proprio tutte</span> — le arti,
        i mestieri, le tradizioni, le ricette, le storie del territorio della Valdelsa.
        Digitalizzate. Preservate. Pronte per essere tramandate.
      </p>
      <p className="text-zinc-400 text-base leading-relaxed mb-8 max-w-3xl mx-auto">
        Immagina la fine del mondo. Hai un'arca. Cosa ci metti dentro?
        Noi ci mettiamo il sapere. Ogni video, ogni foto, ogni intervista a un Senatore
        che racconta come si faceva il formaggio nel '52, come si batteva il grano,
        come si tesseva la lana. <span className="text-white font-bold">Noi facciamo da ponte</span> fra le cose antiche
        e il futuro iperconnesso.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        {[
          { title: 'Registra', desc: 'Video-interviste ai Senatori. Le loro mani, le loro voci, i loro segreti. Prima che sia troppo tardi.', color: '#D4A574' },
          { title: 'Digitalizza', desc: 'Ogni tecnica, ogni ricetta, ogni procedimento. Catalogato, indicizzato, ricercabile. L\'arca del sapere.', color: '#00FF41' },
          { title: 'Tramanda', desc: 'Un ragazzino di 12 anni apre l\'app e impara a fare un cesto di vimini. Missione compiuta.', color: '#FFD700' },
        ].map(s => (
          <div key={s.title} className="bg-[#0A0A0A] border rounded-xl p-6 text-left" style={{ borderColor: `${s.color}30` }}>
            <h3 className="font-mono font-black text-xl mb-2" style={{ color: s.color }}>{s.title}</h3>
            <p className="text-zinc-400 text-sm leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>

      <div className="border rounded-xl p-6 text-center" style={{ borderColor: '#00FF4130', background: '#00FF4108' }}>
        <p className="font-mono font-black text-base text-[#00FF41] mb-2">
          Geniale? Noi pensiamo di si.
        </p>
        <p className="text-zinc-400 text-sm max-w-xl mx-auto">
          La Biblioteca Digitale della Valdelsa sara il primo archivio vivente
          delle arti e tradizioni di un territorio intero. Open source. Per sempre.
        </p>
      </div>
    </div>

    {/* AFORISMA 4 */}
    <Aforisma text="Chi non conosce il passato e condannato a ripeterlo. Chi non lo tramanda e condannato a perderlo." author="Il Covo" />

    {/* ======================================== */}
    {/* IL FESTIVAL — VISTA AEREA */}
    {/* ======================================== */}
    <Img src={IMG.festival} alt="ARTISTIGIANATO vista aerea" overlay="bg-gradient-to-t from-[#050505] via-black/40 to-black/20" h="min-h-[50vh]" id="festival-overview">
      <div className="w-full text-center px-4 mt-auto pb-10">
        <h2 className="text-3xl md:text-4xl font-black font-mono text-white mb-3" style={{ textShadow: '0 2px 30px rgba(0,0,0,0.8)' }}>
          Il Festival
        </h2>
        <p className="text-white/80 text-sm md:text-base max-w-2xl mx-auto mb-4">
          Meta settembre. La Valdelsa si trasforma. Ogni piazza, ogni vicolo, ogni cortile diventa
          un laboratorio a cielo aperto. Tre giorni di arti, mestieri, storie e sapori.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <span className="bg-white/10 backdrop-blur-sm border border-white/20 text-white font-mono text-xs px-4 py-2 rounded-full">3 Giorni</span>
          <span className="bg-white/10 backdrop-blur-sm border border-white/20 text-white font-mono text-xs px-4 py-2 rounded-full">12+ Botteghe</span>
          <span className="bg-white/10 backdrop-blur-sm border border-white/20 text-white font-mono text-xs px-4 py-2 rounded-full">Tutte le Eta</span>
          <span className="bg-white/10 backdrop-blur-sm border border-white/20 text-white font-mono text-xs px-4 py-2 rounded-full">Ingresso Libero</span>
        </div>
      </div>
    </Img>

    {/* SBANDIERATORI BREAK */}
    <Img src={IMG.sbandieratori} alt="Sbandieratori" overlay="bg-black/40" h="min-h-[25vh]">
      <div className="w-full text-center px-4">
        <p className="text-white font-mono font-black text-base md:text-xl" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}>
          La tradizione non si conserva in un museo. Si vive nelle piazze.
        </p>
      </div>
    </Img>

    {/* AFORISMA 5 */}
    <Aforisma text="Il futuro appartiene a chi conosce la storia delle proprie mani." author="Il Covo" />

    {/* ======================================== */}
    {/* TIMELINE — DAL MEDIOEVO A OGGI */}
    {/* ======================================== */}
    <div className="max-w-4xl mx-auto px-4 py-16" data-testid="timeline">
      <div className="text-center mb-12">
        <p className="text-[#D4A574] font-mono text-xs uppercase tracking-[0.3em] mb-2">Dal Medioevo a Oggi</p>
        <h2 className="text-2xl md:text-3xl font-black font-mono text-white">
          Storie di un tempo che fu
        </h2>
      </div>

      <div className="space-y-0">
        {[
          { era: 'Etruschi', year: 'VII sec. a.C.', text: 'Ceramica, terracotta, oreficeria. Le prime mani artigiane della Valdelsa.', color: '#8B4513' },
          { era: 'Medioevo', year: 'X-XIV sec.', text: 'Fabbri, armaioli, muratori. Le torri che vedi ancora oggi sono opera loro.', color: '#A0522D' },
          { era: 'Rinascimento', year: 'XV-XVI sec.', text: 'Vetro cristallo, tessitura fine, ceramica artistica. La Valdelsa diventa bottega del mondo.', color: '#CD853F' },
          { era: 'Ottocento', year: 'XIX sec.', text: 'Cappelli di paglia, cestai, calzolai. Ogni famiglia ha un mestiere, ogni mestiere una storia.', color: '#D4A574' },
          { era: 'Novecento', year: 'XX sec.', text: 'Cucina contadina, conserve, orto. Le nonne che sfamano famiglie intere con niente.', color: '#DEB887' },
          { era: 'Oggi', year: 'XXI sec.', text: 'ARTISTIGIANATO. Il festival che raccoglie tutto e lo rimette nelle mani dei giovani.', color: '#00FF41' },
        ].map((item, i) => (
          <div key={item.era} className="flex gap-6 items-start py-6 border-l-2 pl-6 ml-4" style={{ borderColor: item.color }}>
            <div className="min-w-[90px]">
              <span className="font-mono font-black text-sm block" style={{ color: item.color }}>{item.era}</span>
              <span className="text-zinc-600 font-mono text-[10px]">{item.year}</span>
            </div>
            <p className="text-zinc-300 text-sm leading-relaxed">{item.text}</p>
          </div>
        ))}
      </div>
    </div>

    {/* FONTI MEDIEVALI BREAK */}
    <Img src={IMG.fonti} alt="Le Fonti medievali" overlay="bg-black/50" h="min-h-[25vh]">
      <div className="w-full text-center px-4">
        <p className="text-white font-mono font-black text-base md:text-xl" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}>
          L'acqua scorre da secoli. Come il sapere, se qualcuno lo raccoglie.
        </p>
      </div>
    </Img>

    {/* AFORISMA 6 */}
    <Aforisma text="In un mondo che dimentica tutto in 24 ore, ricordare e un atto di ribellione." author="Il Covo" />

    {/* ======================================== */}
    {/* CTA */}
    {/* ======================================== */}
    <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="artistigianato-cta">
      <div className="border border-[#D4A574]/30 rounded-xl p-8 md:p-12" style={{ background: 'linear-gradient(180deg, #D4A57408, #05050500)' }}>
        <p className="text-[#00FF41] font-mono text-xs uppercase tracking-[0.3em] mb-1" style={{ animation: 'glowPulse 3s ease-in-out infinite' }}>
          &lt;/AI_covo.tech&gt;&reg;
        </p>
        <p className="text-[#00FF41]/50 font-mono text-[9px] uppercase tracking-[0.3em] mb-3">DIGITAL HUB SOCIALE</p>
        <h2 className="text-2xl md:text-3xl font-black font-mono text-white mb-3">
          ARTIST<span className="text-[#D4A574]">IGIANATO</span>
        </h2>
        <p className="text-zinc-400 text-sm mb-2">Meta Settembre — Valdelsa</p>
        <p className="text-zinc-400 text-sm max-w-md mx-auto mb-6">
          Ideato da <span className="text-white font-bold">Francesco Sozzi</span> — Maieutica.<br />
          Un progetto &lt;/AI_covo.tech&gt;&reg; per la Valdelsa.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/dona" data-testid="artistigianato-sostieni" className="inline-block bg-[#D4A574] text-black font-mono font-black text-base px-8 py-3 rounded-lg hover:bg-[#E0B88A] transition-colors">
            SOSTIENI IL FESTIVAL
          </Link>
          <Link to="/doposcuola" data-testid="artistigianato-covo" className="inline-block bg-transparent border-2 border-[#00FF41] text-[#00FF41] font-mono font-black text-base px-8 py-3 rounded-lg hover:bg-[#00FF41]/10 transition-colors">
            SCOPRI IL COVO
          </Link>
        </div>
      </div>
    </div>

    {/* FINAL IMAGE — CASTELLO */}
    <Img src={IMG.castello} alt="Castello della Valdelsa" overlay="bg-black/40" h="min-h-[30vh]">
      <div className="w-full text-center px-4">
        <p className="text-white font-mono font-black text-lg md:text-2xl" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}>
          Il sapere non muore. Se qualcuno lo tramanda.
        </p>
        <p className="text-[#00FF41] font-mono text-xs mt-3" style={{ animation: 'glowPulse 3s ease-in-out infinite' }}>
          &lt;/AI_covo.tech&gt;&reg; — DIGITAL HUB SOCIALE — Valdelsa
        </p>
      </div>
    </Img>
  </div>
);

export default ArtistigianatoPage;
